/* eslint-disable */
import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as SortAsc } from '../../assets/icons/sort-ascending.svg';
import { ReactComponent as SortDesc } from '../../assets/icons/sort-descending.svg';
import { ReactComponent as SortDefault } from '../../assets/icons/sort-icon.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import NoDataFound from '../../assets/images/no-data-found.png';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import HighlightText from '../../shared-functions/HighlightText';
import BatteryLoader from '../battery-loader/BatteryLoader';
import Pagination from '../pagination/Pagination';
import TopHeaderButtons from '../top-header-buttons/TopHeaderButtons';

import CustomTableHeader from './components/TableHeader';
import CustomTableHeaderV2 from './components/TableHeaderV2';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  clearSidePanelData,
  removeSidePanelData,
  setSidePanelData,
} from '../../redux/slice/TableSidePanelSlice';

type RowWithId = { id: string; [key: string]: any }; // Ensure id is either string or number

// Define ColumnHeaderProps to be generic and flexible for any data row structure
type ColumnHeaderProperties<T> = {
  label: string;
  sortAllow?: boolean;
  customLabel?: JSX.Element;
  accessorKey: keyof T; // Tied to the keys of the data row (T)
  customData?: (currentRow: T, index: number) => JSX.Element | null; // Custom rendering function for the data
  columnWidth?: number;
  alignDataToRight?: boolean;
  allowColumnResize?: boolean; // By default it will be undefined
  queryParamsKey?: string; // This is used to push a value to search query params
  queryParamsValue?: string; // If not passed, query params will take row id by default
  sidePanelComponent?: JSX.Element;
  hideWhenSidePanelOpen?: boolean; // If true, this row will be hidden when side panel is open
  handleClickExpandView?: (currentRow: any, newTab: boolean) => void; // Function to expand side panel view in full screen
};

interface CustomTableProperties<T extends RowWithId> {
  dataKey?: string;
  tableHeight?: string;
  paginate?: boolean;
  loading?: boolean;
  header: ColumnHeaderProperties<T>[]; // Header configuration for the table
  data: T[]; // Array of data representing rows in the table
  currentPage: number;
  handlePageChange: (pageNumber: number) => void;
  handleLimitChange: (changeLimit: number) => void;
  handleSortChange?: (change: any) => void;
  sortData?: {
    sortBy: string;
    sortField: string;
  };
  footerLeft?: JSX.Element;
  filterFunction?: () => void;
  filterClearFunction?: () => void;
  leftTopContent?: JSX.Element;
  rightTopContent?: JSX.Element;
  sizeChangeButtonRequired?: boolean;
  topHeader?: boolean;
  filterAllow?: boolean;
  page?: number;
  totalResults?: number;
  totalPages?: number;
  limit?: number;
  setHeightAutoToTr?: boolean;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading?: boolean;
  filterListToDispatch?: AsyncThunk<any, any, any>;
  refreshRequired?: boolean;
  handleRefresh?: () => void;
  hoverLabel?: string;
  exportDataPdfOrCSV?: (type: 'pdf' | 'csv') => void;
  exportData?: any[];
  exportLoader?: boolean;
  version?: string;
  refreshCurrentPages?: () => void;
  serialNo?: boolean;
  rowClickEvent?: (id: string, row: T) => void;
  rowClickParamsKey?: string;
  rowClickSidePanel?: JSX.Element;
  rowClassName?: (row: T) => string;
  onClearFilterChange?: () => void;
  checkbox?: boolean;
  selectedRows?: string[];
  onSelectedRowsChange?: (selected: string[]) => void;
  selectKey?: keyof T;
  flexDirection?: boolean;
  onHiddenColumnsChange?: (hiddenColumns: string[]) => void;
  sidePanelTopLeftContent?: JSX.Element;
  sidePanelManualContent?: JSX.Element;
  sidePanelEnabled?: boolean;
  onSidePanelClose?: () => void;
  explore?: boolean;
  oldVersion?: boolean;
}

/**
 * CustomTable component
 */
function CustomTable<T extends RowWithId>(properties: CustomTableProperties<T>) {
  const {
    dataKey,
    tableHeight,
    paginate,
    loading,
    header,
    data,
    currentPage,
    limit = 0,
    totalPages,
    totalResults,
    handlePageChange,
    handleLimitChange,
    handleSortChange = () => {},
    sortData,
    leftTopContent,
    rightTopContent,
    filterFunction = () => {},
    filterClearFunction = () => {},
    sizeChangeButtonRequired,
    filterAllow,
    topHeader,
    footerLeft,
    setHeightAutoToTr,
    filters,
    filtersToDispatch,
    filterListLoading,
    filterListToDispatch,
    refreshRequired,
    handleRefresh,
    hoverLabel,
    exportDataPdfOrCSV,
    exportData,
    exportLoader,
    version,
    refreshCurrentPages,
    serialNo,
    rowClickEvent,
    rowClickParamsKey,
    rowClickSidePanel,
    rowClassName,
    onClearFilterChange,
    checkbox,
    selectedRows = [],
    onSelectedRowsChange = () => {},
    selectKey = '_id',
    flexDirection,
    onHiddenColumnsChange,
    sidePanelTopLeftContent,
    sidePanelManualContent,
    sidePanelEnabled,
    onSidePanelClose,
    explore = true,
    oldVersion,
  } = properties;

  const { t, i18n } = useTranslation('customTable');
  const dispatch = useAppDispatch();
  const { activeRow } = useAppSelector((state) => state.tableSidePanel);
  const [selectedRowId, setSelectedRowId] = useState('');
  const [textToHighlight, setTextToHighlight] = useState<string | null>(null);
  const [tableSize, setTableSize] = useState<'small' | 'medium' | 'large'>('small');
  const [sidePanelDetails, setSidePanelDetails] = useState({
    jsxElement: <div />,
    enableExpandFullScreen: false,
  });

  // -------------- View & Hide column functionality ------------------ //
  const [hidedColumn, setHidedColumn] = useState<string[]>([]);

  // Code to handle params related functions
  const [searchParameters, setSearchParameters] = useSearchParams();
  // Removed sidePanelDetails state to fix stale content issue

  const [widthOfContainers, setWidthOfContainers] = useState({
    left: 100, // percentage
    right: 0,
    applyTransitionEffect: true,
  });

  const initialWidths = header.reduce(
    (accumulator, col) => {
      return {
        ...accumulator,
        [col.accessorKey.toString()]: col.columnWidth || 140,
      };
    },
    {} as { [key: string]: number },
  );

  if (serialNo) {
    initialWidths.serialNo = 50;
  }
  const [columnWidths, setColumnWidths] = useState(initialWidths);

  /**
   * Function to handle page number change
   * @param pageNumber
   */
  const handlePageChangeInTable = (pageNumber: number) => {
    if (!loading) {
      handlePageChange(pageNumber);
    }
  };

  /**
   * Function to handle limit change
   * @param pageNumber
   */
  const handleLimitChangeInTable = (changeLimit: number) => {
    if (!loading) {
      handleLimitChange(changeLimit);
    }
  };

  /**
   * handleCheckRow
   * @param row
   */
  const handleSelectAll = () => {
    const pageIds = data.map((row) => String(row[selectKey]));

    const isAllSelected = pageIds.every((id) => selectedRows.includes(id));

    if (isAllSelected) {
      onSelectedRowsChange(selectedRows.filter((id) => !pageIds.includes(id)));
    } else {
      onSelectedRowsChange([
        ...selectedRows,
        ...pageIds.filter((id) => !selectedRows.includes(id)),
      ]);
    }
  };

  /**
   * handleCheckRow
   * @param row
   */
  const handleCheckRow = (row: T) => {
    const value = String(row[selectKey]);

    if (selectedRows.includes(value)) {
      onSelectedRowsChange(selectedRows.filter((item) => item !== value));
    } else {
      onSelectedRowsChange([...selectedRows, value]);
    }
  };

  /**
   * Function to handle sort field
   * @param pageNumber
   */
  const handleSortFieldTable = (changeLimit: string) => {
    if (!loading) {
      handleSortChange({
        ...sortData,
        sortField: changeLimit,
        sortBy: sortData?.sortField === changeLimit && sortData.sortBy === 'asc' ? 'desc' : 'asc',
      });
    }
  };

  /**
   * Function to handle table size change
   */
  const handleChangeTableSize = () => {
    if (tableSize === 'small') {
      setTableSize('medium');
    } else if (tableSize === 'medium') {
      setTableSize('large');
    } else if (tableSize === 'large') {
      setTableSize('small');
    }
  };

  const containerReference = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  /**
   * handleMouseDown
   * @param event
   * @param key
   */
  const handleColumnMouseDown = (event: React.MouseEvent, key: string) => {
    const startX = event.clientX;
    // eslint-disable-next-line no-template-curly-in-string
    const startWidth = Number(columnWidths[key]);

    /**
     * handleMouseMove
     * @param event
     * @param key
     */
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const columnMovement = moveEvent.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + columnMovement, 40), 1000); // Ensure a minimum width of 40px and maximum width of 1000px

      setColumnWidths({
        ...columnWidths,
        [key]: newWidth,
      });
    };

    /**
     * handleMouseUp
     * @param event
     * @param key
     */
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Function to handle mouse move event
   */
  const handleMouseDown = () => {
    isDragging.current = true;
  };

  /**
   * Function to handle mouse move event
   * @param e
   * @returns
   */
  const handleMouseMove = (event: MouseEvent) => {
    if (!isDragging.current || !containerReference.current) return;

    const containerWidth = containerReference.current.offsetWidth;

    const offsetX = event.clientX - containerReference.current.getBoundingClientRect().left;

    const left = (offsetX / containerWidth) * 100;
    const right = 100 - left;

    if (left > 5 && right > 5) {
      setWidthOfContainers({ left, right, applyTransitionEffect: false });
    }
  };

  /**
   * Function to handle mouse move event
   */
  const handleMouseUp = () => {
    isDragging.current = false;
  };

  /**
   * Add or update a specific param, while keeping others
   * @param key
   * @param value
   */
  const updateQueryParameter = (key: string, value: string) => {
    const parameters = new URLSearchParams(); // start fresh, no existing params
    parameters.set(key, value);
    setSearchParameters(parameters);
  };

  /**
   * Function to handle side panel expand view
   */
  const handleSidePanelExpand = (newTab: boolean) => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert params -> object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const urlKey = Object.keys(parametersObject)[0];

    // find correct header config for this param
    const activeSidePanelHeader = header.find(
      (eachHeader) =>
        eachHeader.queryParamsKey === urlKey || eachHeader.queryParamsValue === urlKey,
    );

    if (activeSidePanelHeader?.handleClickExpandView) {
      const value = parametersObject[urlKey];

      // Resolve correct row using id OR custom value selector
      let item =
        data.find((object) => object.id === value) ||
        data.find((object) => object[activeSidePanelHeader.queryParamsValue || 'id'] === value);

      activeSidePanelHeader.handleClickExpandView(item || null, newTab);
    }
  };

  /**
   * Function to handle table click event
   * @param id
   * @returns
   */
  const handleRowClickEvent = (row: any) => {
    if (!rowClickEvent || !rowClickParamsKey) return;
    const parameterValue = row[rowClickParamsKey] || row.id; // <-- dynamic param
    rowClickEvent(parameterValue, row);
  };

  /**
   * Handling row click
   * @param row
   * @param col
   */
  const handleRowClick = (row: any, col: any) => {
    if (col?.queryParamsKey && col?.sidePanelComponent) {
      const queryValue = col.queryParamsValue ? row[col.queryParamsValue] : undefined;
      updateQueryParameter(col.queryParamsKey.toString(), queryValue || row.id);
      dispatch(
        setSidePanelData({
          key: dataKey,
          value: row.id,
        }),
      );
    }
  };

  /**
   * Function to close side-panel
   */
  const handleCloseSidePanel = () => {
    if (onSidePanelClose) onSidePanelClose();
    setSearchParameters((prev) => {
      const updated = new URLSearchParams(prev.toString());

      // 1️⃣ remove all header query keys
      header.forEach((col) => {
        if (col.queryParamsKey) {
          updated.delete(col.queryParamsKey);
        }
      });

      // 2️⃣ remove rowClick param key
      if (rowClickParamsKey) {
        updated.delete(rowClickParamsKey);
      }

      // 3️⃣ ALSO remove manual side panel param like ?add=facility
      updated.delete('add');

      return updated;
    });

    // close panel UI
    setWidthOfContainers({
      left: 100,
      right: 0,
      applyTransitionEffect: true,
    });

    // clear redux side panel state
    dispatch(clearSidePanelData({ key: dataKey }));
  };

  // ---------------------------------------------------------------------------
  // Derive side panel content directly from props/state (Fix for async updates)
  // ---------------------------------------------------------------------------
  let activeSidePanelContent = null;
  let enableExpandFullScreen = false;

  const parameters = new URLSearchParams(searchParameters);
  const parametersObject: Record<string, string> = {};
  parameters.forEach((value, key) => {
    parametersObject[key] = value;
  });

  const jsxElementToShow = header.find(
    (eachHeader) => eachHeader.queryParamsKey === Object.keys(parametersObject)[0],
  );

  // 1. Manual Mode (e.g. Add form)
  if (sidePanelEnabled) {
    if (sidePanelManualContent) {
      activeSidePanelContent = sidePanelManualContent;
      enableExpandFullScreen = false;
    }
  }
  // 2. Column Click Mode
  else if (parametersObject && jsxElementToShow && jsxElementToShow.sidePanelComponent) {
    activeSidePanelContent = jsxElementToShow.sidePanelComponent;
    enableExpandFullScreen = Boolean(jsxElementToShow.handleClickExpandView);
  }
  // 3. Row Click Mode
  else if (
    !jsxElementToShow &&
    rowClickSidePanel &&
    rowClickParamsKey &&
    Object.keys(parametersObject)?.includes(rowClickParamsKey)
  ) {
    activeSidePanelContent = rowClickSidePanel;
    enableExpandFullScreen = false;
  }

  // ---------------------------------------------------------------------------
  // Effect to manage width animations based on presence of side panel content
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (sidePanelEnabled) {
      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });
    } else if (
      (parametersObject && jsxElementToShow && jsxElementToShow.sidePanelComponent) ||
      (!jsxElementToShow &&
        rowClickSidePanel &&
        rowClickParamsKey &&
        Object.keys(parametersObject)?.includes(rowClickParamsKey))
    ) {
      setWidthOfContainers({
        left: parameters?.size === 1 ? 60 : 100,
        right: parameters?.size === 1 ? 40 : 0,
        applyTransitionEffect: true,
      });
    } else if (parameters?.size === 0) {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [sidePanelEnabled, searchParameters, jsxElementToShow, rowClickSidePanel, rowClickParamsKey]);

  useEffect(() => {
    if (filters?.length) {
      const searchBy = filters.find((eachFilter) => eachFilter.searchBy);
      setTextToHighlight(searchBy?.value?.toString() || null);
    }
  }, [filters]);

  useEffect(() => {
    const parameters = new URLSearchParams(searchParameters);

    // Convert URLSearchParams to a plain object
    const parametersObject: Record<string, string> = {};
    parameters.forEach((value, key) => {
      parametersObject[key] = value;
    });

    const jsxElementToShow = header.find(
      (eachHeader) =>
        eachHeader.queryParamsKey === Object.keys(parametersObject)[0] &&
        eachHeader.sidePanelComponent,
    );

    // If column click side panel was not found then, check for row click
    if (
      !jsxElementToShow &&
      rowClickSidePanel &&
      rowClickParamsKey &&
      Object.keys(parametersObject)?.includes(rowClickParamsKey)
    ) {
      setSidePanelDetails({
        jsxElement: rowClickSidePanel || <div>{t('somethingWentWrong')}</div>,
        enableExpandFullScreen: false, // Need to handle navigate full screen icon if required
      }); // If required, need to show the proper error message

      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });
    }

    if (parametersObject && jsxElementToShow) {
      setWidthOfContainers({
        left: parameters?.size === 1 ? 60 : 100,
        right: parameters?.size === 1 ? 40 : 0,
        applyTransitionEffect: true,
      });

      setSidePanelDetails({
        jsxElement: jsxElementToShow.sidePanelComponent || <div>{t('somethingWentWrong')}</div>,
        enableExpandFullScreen: Boolean(jsxElementToShow.handleClickExpandView),
      }); // If required, need to show the proper error message
    }

    if (parameters?.size === 0) {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [searchParameters]);

  useEffect(() => {
    if (sidePanelEnabled) {
      setWidthOfContainers({
        left: 60,
        right: 40,
        applyTransitionEffect: true,
      });

      if (sidePanelManualContent) {
        setSidePanelDetails({
          jsxElement: sidePanelManualContent,
          enableExpandFullScreen: false,
        });
      }
    } else {
      setWidthOfContainers({
        left: 100,
        right: 0,
        applyTransitionEffect: true,
      });
    }
  }, [sidePanelEnabled]);

  // useEffect to handle side-panel open and close
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const activeKeyValue = activeRow.find((row) => row.key === dataKey);

    if (activeRow) setSelectedRowId(activeKeyValue?.value || '');
  }, [dataKey, activeRow, searchParameters]);

  useEffect(() => {
    if (onHiddenColumnsChange) {
      onHiddenColumnsChange(hidedColumn);
    }
  }, [hidedColumn]);

  return (
    <div className={`custom-table-wrap ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      {/* <div style={{ display: 'none' }}> */}
      {topHeader && oldVersion ? (
        <TopHeaderButtons
          filterFunction={filterFunction}
          filterClearFunction={filterClearFunction}
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
          filterAllow={filterAllow}
          refreshRequired
          handleRefresh={handleRefresh}
          hoverLabel={t('refresh')}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={exportData}
          exportLoader={exportLoader}
          labelForHideColumn={header.map((eachHeader) => ({
            label: eachHeader.label,
            accessorKey: eachHeader.accessorKey,
            disableHideFunc: false,
          }))}
          hidedColumn={hidedColumn}
          setHidedColumn={setHidedColumn}
        />
      ) : null}
      {version === 'v2' && filters && filterListToDispatch ? (
        <CustomTableHeaderV2
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filters={filters}
          filtersToDispatch={filtersToDispatch}
          filterListToDispatch={filterListToDispatch}
          filterListLoading={filterListLoading || false}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={exportData || data}
          exportLoader={exportLoader}
          labelForHideColumn={header.map((eachHeader) => ({
            label: eachHeader.label,
            accessorKey: eachHeader.accessorKey,
            disableHideFunc: false,
          }))}
          hidedColumn={hidedColumn}
          setHidedColumn={setHidedColumn}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
          refreshCurrentPages={refreshCurrentPages}
          onClearFilterChange={onClearFilterChange}
          flexDirection={flexDirection}
        />
      ) : null}
      {version === 'v1' && filters && filterListToDispatch ? (
        <CustomTableHeader
          currentFontSize={tableSize}
          sizeChangeButtonRequired={sizeChangeButtonRequired}
          sizeChangeFunction={handleChangeTableSize}
          filters={filters}
          filtersToDispatch={filtersToDispatch}
          filterListToDispatch={filterListToDispatch}
          filterListLoading={filterListLoading || false}
          refreshRequired={refreshRequired}
          handleRefresh={handleRefresh}
          hoverLabel={hoverLabel}
          leftTopContent={leftTopContent}
          rightTopContent={rightTopContent}
        />
      ) : null}
      <div
        className="custom-table-with-side-panel"
        ref={containerReference}
        style={{ '--table-height': tableHeight } as React.CSSProperties}>
        <div
          className="custom-table"
          style={
            {
              '--table-height': tableHeight,
              width: `${widthOfContainers.left}%`,
              transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
            } as React.CSSProperties
          }>
          <table className={`table-container ${tableSize}`}>
            <thead className="table-header-wrap">
              <tr className="table-header-container">
                {checkbox && (
                  <th className="table-header checkbox " style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all rows on this page"
                      checked={data.every((row) => selectedRows.includes(String(row[selectKey])))}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}

                {serialNo && (
                  <th
                    className="table-header"
                    key="serialNo"
                    style={{
                      minWidth: `${Math.max(columnWidths.serialNo, 40)}px`,
                      width: `${columnWidths.serialNo}px`,
                      flex: `${columnWidths.serialNo} 0 auto`,
                    }}>
                    <p>{t('sNo')}</p>
                  </th>
                )}
                {header
                  .filter((col) => !hidedColumn.includes(col.accessorKey.toString()))
                  .filter((col) => !(col.hideWhenSidePanelOpen && widthOfContainers.right > 0))
                  .map((col, index) => (
                    <th
                      className="table-header"
                      key={col.accessorKey as string}
                      style={{
                        minWidth: `${Math.max(columnWidths[col.accessorKey.toString()], 40)}px`,
                        width: `${columnWidths[col.accessorKey.toString()]}px`,
                        flex: `${columnWidths[col.accessorKey.toString()]} 0 auto`,
                      }}>
                      <p>{col.customLabel ? col.customLabel : col.label}</p>
                      {col.sortAllow ? (
                        <div
                          onClick={() => handleSortFieldTable(col.accessorKey as string)}
                          role="presentation">
                          {sortData?.sortField === col.accessorKey ? (
                            (sortData?.sortBy === 'asc' && (
                              <SortAsc className="sort-icon ascending" />
                            )) ||
                            (sortData?.sortBy === 'desc' && (
                              <SortDesc className="sort-icon descending" />
                            ))
                          ) : (
                            <SortDefault />
                          )}
                        </div>
                      ) : null}
                      {index !== header.length - 1 && col?.allowColumnResize !== false && (
                        <div
                          className="table-column-resizer"
                          onMouseDown={(event) =>
                            handleColumnMouseDown(event, col.accessorKey.toString())
                          }
                          role="presentation"
                        />
                      )}
                    </th>
                  ))}
              </tr>
            </thead>
            {(() => {
              let tableView = null;
              if (loading) {
                tableView = (
                  <div className="table-loading">
                    <BatteryLoader />
                  </div>
                );
              } else if (data?.length > 0 && !loading) {
                tableView = (
                  <tbody className="table-body">
                    {data.map((row, index) => (
                      <tr
                        role="presentation"
                        onClick={() => handleRowClickEvent(row)}
                        className={`table-row ${selectedRowId === row?.id ? 'activeRow' : ''} ${rowClassName ? rowClassName(row) : ''}`}
                        key={row.id}
                        style={
                          {
                            height: setHeightAutoToTr && 'auto',
                            '--header-length': header?.length || 1,
                          } as React.CSSProperties
                        }>
                        {checkbox && (
                          <td className="table-row-data checkbox" style={{ width: '50px' }}>
                            <input
                              type="checkbox"
                              aria-label="Select row"
                              checked={selectedRows.includes(String(row[selectKey]))}
                              onChange={() => handleCheckRow(row)}
                            />
                          </td>
                        )}
                        {serialNo && (
                          <td
                            className="table-row-data"
                            style={{
                              minWidth: `${Math.max(columnWidths.serialNo, 40)}px`,
                              width: `${columnWidths.serialNo}px`,
                              flex: `${columnWidths.serialNo} 0 auto`,
                            }}>
                            <p className="table-row-each-data">
                              {(currentPage - 1) * limit + index + 1}
                            </p>
                          </td>
                        )}
                        {header
                          .filter((col) => !hidedColumn.includes(col.accessorKey.toString()))
                          .filter(
                            (col) => !(col.hideWhenSidePanelOpen && widthOfContainers.right > 0),
                          )
                          .map((col) => (
                            <td
                              className="table-row-data"
                              key={col.accessorKey as string}
                              onClick={
                                col?.queryParamsKey && col?.sidePanelComponent
                                  ? (e) => {
                                      e.stopPropagation();
                                      handleRowClick(row, col);
                                    }
                                  : undefined
                              }
                              style={{
                                padding: setHeightAutoToTr ? '5px 5px 5px 5px' : '',
                                minWidth: `${Math.max(columnWidths[col.accessorKey.toString()], 40)}px`,
                                width: `${columnWidths[col.accessorKey.toString()]}px`,
                                flex: `${columnWidths[col.accessorKey.toString()]} 0 auto`,
                                cursor:
                                  col?.queryParamsKey && col?.sidePanelComponent
                                    ? 'pointer'
                                    : undefined,
                              }}>
                              {col.customData ? (
                                col.customData(row, index)
                              ) : (
                                // Use type assertion here to indicate that row[col.accessorKey] is valid for rendering
                                <p
                                  className={`table-row-each-data ${col?.queryParamsKey && col?.sidePanelComponent ? 'hyperlink' : ''}`}
                                  role="presentation">
                                  {HighlightText(
                                    textToHighlight || null,
                                    row[col.accessorKey]?.toString() || '', // Empty quotes '' -> To avoid showing undefined
                                  )}
                                </p>
                              )}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                );
              } else if (data?.length === 0 && !loading) {
                tableView = (
                  <div className="table-no-data-wrap">
                    <img src={NoDataFound} alt="" className="no-data-found-image" />
                    <div className="no-data-found-details">
                      <h3>{t('nothingHereTitle')}</h3>
                      <p>{t('nothingHereMessage')}</p>
                    </div>
                  </div>
                );
              }
              return tableView;
            })()}
          </table>
        </div>
        {widthOfContainers.right > 0 ? (
          <div className="adjustable-divider" role="presentation" onMouseDown={handleMouseDown} />
        ) : (
          <div />
        )}
        {widthOfContainers.right > 0 ? (
          <div
            className="custom-table-side-panel"
            style={{
              width: `${widthOfContainers.right}%`,
              transition: `width ${widthOfContainers.applyTransitionEffect ? '0.2s' : '0.0s'} ease-in-out`,
            }}>
            <div className="side-panel-header">
              {sidePanelDetails.enableExpandFullScreen && explore && (
                <div
                  className={`side-panel-header-left ${sidePanelDetails.enableExpandFullScreen ? 'active' : 'in-active'}`}>
                  <span role="presentation" onClick={() => handleSidePanelExpand(false)}>
                    <ExpandIcon />
                  </span>
                  <span role="presentation" onClick={() => handleSidePanelExpand(true)}>
                    <NewTabIcon />
                  </span>
                </div>
              )}
              <div className="side-panel-header-left">{sidePanelTopLeftContent}</div>

              <span role="presentation" className="close-button" onClick={handleCloseSidePanel}>
                <CloseIcon />
              </span>
            </div>
            <div className="side-panel-content">{activeSidePanelContent}</div>
          </div>
        ) : (
          <div />
        )}
      </div>
      <div className="custom-table-footer">
        <div className="footer-left">{footerLeft}</div>
        <div className="footer-right">
          {paginate ? (
            <Pagination
              activePage={currentPage}
              handlePageChange={handlePageChangeInTable}
              handleLimitChange={handleLimitChangeInTable}
              limit={limit}
              size={totalPages}
              totalResults={totalResults}
            />
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}

CustomTable.defaultProps = {
  tableHeight: '100%',
  paginate: false,
  footerLeft: null,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  limit: 0,
  sizeChangeButtonRequired: true,
  topHeader: true,
  /**
   * Filter function
   */
  filterFunction: () => {},
  /**
   * Filter Clear function
   */
  filterClearFunction: () => {},
  leftTopContent: null,
  rightTopContent: null,
  loading: false,
  filterAllow: true,
  flexDirection: false,
  version: 'v2',
  oldVersion: false,
};

export default CustomTable;
